import { LogLevels } from '../constants/common';
import type { LogLevel } from '../types/common';

declare global {
  interface Window {
    _sdkMarks: Set<string>;
  }
}

type PlainObject = Record<string, unknown>;

export function isObject(value: unknown): value is PlainObject {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function deepEqualObject(object1: PlainObject, object2: PlainObject): boolean {
  const keysOfObject1 = Object.keys(object1);
  const keysObObject2 = Object.keys(object2);

  if (keysOfObject1.length !== keysObObject2.length) {
    return false;
  }

  let isEquals = true;

  keysOfObject1.forEach((key) => {
    const object1Element = object1[key];
    const object2Element = object2[key];
    const areObjects = isObject(object1Element) && isObject(object2Element);

    const isObjectAndNotEquals = areObjects && !deepEqualObject(object1Element, object2Element);
    const isNotObjectAndNotEquals = !areObjects && (object1Element !== object2Element);

    if (isObjectAndNotEquals || isNotObjectAndNotEquals) {
      isEquals = false;
    }
  });

  return isEquals;
};

export function debounce<T extends(...args: never[]) => Promise<void>>(callback: T, delay: number, immediate = false) {
  let timeout: ReturnType<typeof setTimeout> | null = null;

  return async function debounced(this: unknown, ...args: Parameters<typeof callback>): Promise<void> {
    const later = async (): Promise<void> => {
      timeout = null;

      if (!immediate) {
        await callback.apply(this, args);
      }
    };

    const callNow = immediate && !timeout;

    if (typeof timeout === 'number') {
      clearTimeout(timeout);
    }

    timeout = setTimeout(later, delay);

    if (callNow) {
      await callback.apply(this, args);
    }
  };
}

export function prepareLogLevel(level?: LogLevel): LogLevel {
  return level ?? LogLevels.Error;
}

export function isAnalyticsActive(flag?: boolean): boolean {
  return flag ?? true;
}

export function markSDKInstance(mark: string): void {
  window._sdkMarks ||= new Set<string>();
  window._sdkMarks.add(mark);
}

export function getSDKInstancesMarks(): string[] {
  return Array.from(window._sdkMarks || []);
}

export async function safeCall<T>(
  handler: () => Promise<T>,
  onError?: (error: unknown) => void,
): Promise<T | undefined> {
  try {
    return await handler();
  } catch (error) {
    onError?.(error);

    return undefined;
  }
}
